// src/pages/RoleManagement.jsx
import { useEffect, useState, Fragment } from 'react';
import { api } from '../lib/api.js';

export default function RoleManagement() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  async function load() {
    try {
      setData(await api('/admin/roles-permissions'));
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggle(roleId, permissionId, currentlyGranted) {
    try {
      await api('/admin/roles-permissions/toggle', {
        method: 'POST', body: JSON.stringify({ roleId, permissionId, grant: !currentlyGranted }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (error) return <div className="p-8 text-p1 text-sm">{error}</div>;
  if (!data) return <div className="p-8 text-ink/50 text-sm">Loading…</div>;

  const hasPermission = (roleId, permId) =>
    data.mappings.some((m) => m.role_id === roleId && m.permission_id === permId);

  const categories = [...new Set(data.permissions.map((p) => p.category))];

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-charcoal">Roles &amp; Permissions</h1>
      <p className="text-xs text-ink/50 mt-1">
        <span className="font-medium">Super Admin</span> has every permission implicitly and isn't shown — it can't be edited or reduced.
      </p>

      <div className="mt-5 overflow-x-auto rounded-lg border border-ink/10 bg-white">
        <table className="text-xs min-w-full">
          <thead className="bg-ink/5 text-left uppercase tracking-wide text-ink/50">
            <tr>
              <th className="px-3 py-2 sticky left-0 bg-ink/5">Permission</th>
              {data.roles.filter((r) => r.code !== 'super_admin').map((r) => (
                <th key={r.id} className="px-3 py-2 text-center whitespace-nowrap">{r.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <Fragment key={cat}>
                <tr className="bg-paper">
                  <td colSpan={data.roles.length} className="px-3 py-1.5 font-semibold text-ink/60 capitalize">{cat}</td>
                </tr>
                {data.permissions.filter((p) => p.category === cat).map((perm) => (
                  <tr key={perm.id} className="border-t border-ink/5">
                    <td className="px-3 py-2 sticky left-0 bg-white font-mono">{perm.code}</td>
                    {data.roles.filter((r) => r.code !== 'super_admin').map((role) => {
                      const granted = hasPermission(role.id, perm.id);
                      return (
                        <td key={role.id} className="px-3 py-2 text-center">
                          <button
                            onClick={() => toggle(role.id, perm.id, granted)}
                            aria-label={`${granted ? 'Revoke' : 'Grant'} ${perm.code} for ${role.name}`}
                            className={`h-4 w-4 rounded ${granted ? 'bg-accent' : 'bg-ink/10 hover:bg-ink/20'} transition-colors`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
